import {Controller} from "@hotwired/stimulus"

// Connects to data-controller="custom-form"
export default class extends Controller {
    // srcInput 타겟 추가
    static targets = [
        "groupSelect", "srcInput",
        "pageUrl", "notionUrl", "embedCode",
        "previewFrame",
        "fieldsContainer", "fieldsHelperText", "allFieldsCheckbox"
    ]

    static values = {
        baseUrl: String,
        basePageUrl: String
    }

    get allFieldCheckboxes() {
        return Array.from(this.fieldsContainerTarget.querySelectorAll('input[type="checkbox"]'));
    }

    initialize() {
        this.params = new URLSearchParams();
    }

    connect() {
        if (this.hasFieldsContainerTarget) {
            this.allFieldCheckboxes.forEach(checkbox => checkbox.checked = true);
        }

        this.update();
    }

    toggleAllFields(event) {
        const isChecked = event.currentTarget.checked;
        this.allFieldCheckboxes.forEach(checkbox => checkbox.checked = isChecked);
        this.update();
    }

    update() {
        // 1. 다른 파라미터(그룹, 테마)를 위한 기본 URLSearchParams 객체를 생성합니다.
        this.params = new URLSearchParams();

        // 구독 그룹 ID 가져오기
        if (this.hasGroupSelectTarget) {
            const groupId = this.groupSelectTarget.value;
            if (groupId) {
                this.params.set('group_id', groupId);
            }
        }

        // 1. name="mode"인 라디오 버튼에서 선택된 값을 가져옵니다.
        const selectedMode = this.element.querySelector('input[name="mode"]:checked')?.value;

        // 2. 값이 있을 경우 (light 또는 dark)에만 'mode' 파라미터를 추가합니다.
        //    '시스템 설정'은 value가 ""이므로 이 조건문을 통과하지 않습니다.
        if (selectedMode) {
            this.params.set('mode', selectedMode);
        }

        if (this.hasSrcInputTarget) {
            const customSrc = this.srcInputTarget.value.trim();
            if (customSrc) {
                this.params.set('src', customSrc);
            }
        }

        const nicknameSetting = this.element.querySelector('input[name="nickname"]:checked')?.value;
        if (nicknameSetting) {
            this.params.set('nickname', nicknameSetting);
        }

        const outlinkSetting = this.element.querySelector('input[name="outlink"]:checked')?.value;
        if (outlinkSetting) {
            this.params.set('outlink', outlinkSetting);
        }

        const guideSetting = this.element.querySelector('input[name="guide"]:checked')?.value;
        if (guideSetting) {
            this.params.set('guide', guideSetting);
        }

        if (this.hasFieldsContainerTarget) {
            const allFields = this.allFieldCheckboxes;
            const checkedFields = allFields.filter(checkbox => checkbox.checked);

            // 1. 전체 필드 수와 체크된 필드 수가 같은지 확인 (모두 선택되었는지)
            const isAllSelected = allFields.length === checkedFields.length;
            const isNoneSelected = checkedFields.length === 0;

            this.fieldsHelperTextTarget.classList.remove('text-orange-600', 'dark:text-orange-400');

            if (isAllSelected) {
                // ✅ [규칙 1] 모두 선택: 파라미터를 추가하지 않음 (서버가 '전체'로 해석)
                this.allFieldsCheckboxTarget.checked = true;
                this.fieldsHelperTextTarget.textContent = "모든 부가 정보를 수집합니다.";

            } else if (isNoneSelected) {
                // ✅ [규칙 3] 모두 해제: 'fields' 파라미터를 빈 값으로 추가 (서버가 '수집 안함'으로 해석)
                // allFields.length > 0 조건은 부가정보 필드가 아예 없는 경우를 대비
                if (allFields.length > 0) {
                    this.params.set('fields', '');
                }

                this.allFieldsCheckboxTarget.checked = false;
                this.fieldsHelperTextTarget.textContent = "부가 정보를 수집하지 않습니다. (기본 항목만 수집)";

            } else {
                // ✅ [규칙 2] 일부 선택: 선택된 필드만 파라미터에 추가
                checkedFields.forEach(checkbox => {
                    this.params.append('fields[]', checkbox.value);
                });

                this.allFieldsCheckboxTarget.checked = false;
                this.fieldsHelperTextTarget.textContent = `${checkedFields.length}개의 부가 정보를 선택하여 수집합니다.`;
            }
        }

        // 2. 각 타겟에 맞는 URL을 생성하여 업데이트합니다.
        this.updatePageUrl();
        this.updateNotionUrl();
        this.updateEmbedCode();
        this.updatePreviewFrame();
    }

    urlWithParams(baseUrl) {
        return this.params.size > 0
            ? baseUrl + '?' + this.params.toString() : baseUrl;
    }

    updatePageUrl() {
        this.pageUrlTarget.value = this.urlWithParams(this.basePageUrlValue);
    }

    updateNotionUrl() {
        this.notionUrlTarget.value = this.urlWithParams(this.baseUrlValue);
    }

    // 웹사이트 임베드 코드 업데이트 로직
    updateEmbedCode() {
        this.embedCodeTarget.value = `<iframe src="${this.urlWithParams(this.baseUrlValue)}" style="width: 100%;height: 100%;" frameborder="0"></iframe>`;
    }

    // 미리보기 Iframe 업데이트 로직
    // 불필요한 리로드를 막기 위해 URL이 변경되었을 때만 src를 업데이트합니다.
    updatePreviewFrame() {
        if (this.previewFrameTarget.src !== this.urlWithParams(this.baseUrlValue)) {
            this.previewFrameTarget.src = this.urlWithParams(this.baseUrlValue);
        }
    }
}